import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format, addDays, subDays } from "date-fns";
import { Modal, Button, Card, Container, Row, Col } from "react-bootstrap";
import { es } from "date-fns/locale";

const Horarios = ({ idUsuario }) => {
  const [odontologas, setOdontologas] = useState([]);
  const [selectedOdontologa, setSelectedOdontologa] = useState("");
  const [horarios, setHorarios] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [turno, setTurno] = useState("");
  const [modalData, setModalData] = useState(null);
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [availableModalData, setAvailableModalData] = useState(null);
  const [formModalData, setFormModalData] = useState(null);
  const [dni, setDni] = useState("");
  const [dniVerificado, setDniVerificado] = useState(false);
  const [pacienteExistente, setPacienteExistente] = useState(null);
  const [nuevoPaciente, setNuevoPaciente] = useState({ nombre: "", correo: "", telefono: "", domicilio: "" });
  const [descripcion, setDescripcion] = useState("");
  const [cancelModalData, setCancelModalData] = useState(null);
  const [cancelSuccessModalData, setCancelSuccessModalData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const debounceTimeout = useRef(null);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchOdontologas = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/odontologas/`);
        setOdontologas(response.data);
      } catch (error) {
        console.error("Error al cargar odontólogas:", error);
      }
    };
    fetchOdontologas();
  }, []);
  
  useEffect(() => {
    if ([1, 2, 3, 4].includes(idUsuario)) {
      setSelectedOdontologa(idUsuario);
    }
  }, [idUsuario]);

  const fetchHorarios = async () => {
    if (selectedOdontologa) {
      try {
        const promises = Array.from({ length: 7 }, (_, i) => {
          const date = format(addDays(startDate, i), "yyyy-MM-dd");
          return axios.get(`${process.env.REACT_APP_BACKEND_URL}/horarios/todos/${selectedOdontologa}?fecha=${date}`);
        });
        const responses = await Promise.all(promises);
        const horariosData = responses.flatMap((response) => response.data);
  
        const updatedHorarios = await Promise.all(
          horariosData.map(async (horario) => {
            if (horario.estado === "agendado") {
              try {
                const consultaResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultas/${horario.id_horario}`);
                const pacienteResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/pacientes/dni/${consultaResponse.data.dni_paciente}`);
                return { ...horario, paciente: pacienteResponse.data };
              } catch (error) {
                console.error("Error al obtener la consulta y el paciente:", error);
                return horario;
              }
            }
            return horario;
          })
        );
  
        setHorarios(updatedHorarios);
        const selected = odontologas.find((o) => o.id === Number(selectedOdontologa));
        setTurno(selected?.turno || "");
      } catch (error) {
        console.error("Error al cargar horarios:", error);
      }
    }
  };
  
  useEffect(() => {
    fetchHorarios();
  }, [selectedOdontologa, startDate, odontologas]);

  useEffect(() => {
    if (dni.length >= 8) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        buscarPacientePorDNI(dni);
      }, 300);
    }
  }, [dni]);

  const buscarPacientePorDNI = async (dni) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/pacientes/dni/${dni}`);
      setPacienteExistente(response.data);
      setDniVerificado(true);
    } catch {
      setPacienteExistente(null);
      setDniVerificado(true);
    }
  };

  const handleDniChange = (e) => {
    setDni(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      buscarPacientePorDNI(dni);
    }
  };

  const handleBlur = () => {
    buscarPacientePorDNI(dni);
  };

  const handleHorarioClick = async (horario, dayString, time) => {
    if (horario) {
      if (horario.estado === "agendado") {
        try {
          const consultaResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/consultas/${horario.id_horario}`);
          const pacienteResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/pacientes/dni/${consultaResponse.data.dni_paciente}`);
          console.log("consulta recibidos:", consultaResponse.data);
          setModalData({ consulta: consultaResponse.data, paciente: pacienteResponse.data, horario: horario });
        } catch (error) {
          console.error("Error al obtener la consulta y el paciente:", error);
        }
      } else if (horario.estado === "disponible") {
        setAvailableModalData({ fecha: dayString, hora: time, id_horario: horario.id_horario });
      }
    } else {
      console.log(`Habilitar horario para fecha ${dayString} y hora ${time}`);
      setConfirmModalData({ fecha: dayString, hora: time, id_medica: selectedOdontologa });
    }
  };

  const handleConfirmHabilitarHorario = async () => {
    if (confirmModalData) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/horarios/nuevo`, {
          fecha: confirmModalData.fecha,
          hora: confirmModalData.hora,
          id_medica: confirmModalData.id_medica,
          estado: "disponible",
        });
        console.log("Horario habilitado:", response.data);
        setConfirmModalData(null);
        fetchHorarios();
      } catch (error) {
        console.error("Error al habilitar horario:", error);
      }
    }
  };

  const agendarConsulta = async () => {
    if (!formModalData) {
      console.error("formModalData is null");
      return;
    }

    const payload = {
      dni,
      id_horario: formModalData.id_horario,
      descripcion,
    };

    if (!pacienteExistente) {
      payload.nombre = nuevoPaciente.nombre;
      payload.correo = nuevoPaciente.correo;
      payload.telefono = nuevoPaciente.telefono;
      payload.domicilio = nuevoPaciente.domicilio;
    }

    try {
      const endpoint = pacienteExistente
        ? `${process.env.REACT_APP_BACKEND_URL}/consultas/existente`
        : `${process.env.REACT_APP_BACKEND_URL}/consultas/nuevo`;

      await axios.post(endpoint, payload);
      setStartDate(new Date());
      setDni("");
      setPacienteExistente(null);
      setNuevoPaciente({ nombre: "", correo: "", telefono: "", domicilio: "" });
      setDescripcion("");
      setFormModalData(null);
    } catch (error) {
      console.error("Error al agendar consulta:", error);
    }
  };

  const handleAbrirFormModal = () => {
    console.log("Opening form modal with data:", availableModalData);
    setFormModalData(availableModalData);
    setAvailableModalData(null);
  };

  const handleDeshabilitarHorario = async () => {
    if (availableModalData) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/horarios/deshabilitar/${availableModalData.id_horario}`);
        console.log("Horario eliminado:", response.data);
        setAvailableModalData(null);
        fetchHorarios();
      } catch (error) {
        console.error("Error al eliminar horario:", error);
      }
    }
  };

  const handleAbrirCancelModal = () => {
    setCancelModalData(modalData);
    setModalData(null);
  };

  const handleCancelarConsulta = async () => {
    if (cancelModalData) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/consultas/${cancelModalData.consulta.idconsultas}`);
        console.log("Consulta cancelada:", response.data);
        setCancelModalData(null);
        setCancelSuccessModalData(cancelModalData);
        fetchHorarios();
      } catch (error) {
        console.error("Error al cancelar consulta:", error);
      }
    }
  };
  const generarMensajeRecordatorio = (consulta) => {
    return `Estimado/a ${consulta.paciente.nombre_paciente}, \n, este es un recordatorio de la consulta que tiene programada para el día ${consulta.horario.fecha} a las ${consulta.horario.hora} con la ${selectedOdontologa ? odontologas.find((od) => od.id === selectedOdontologa)?.nombre : ""}. \nSaludos cordiales. Lash Odontología integral `;
  };

  const obtenerLinkWhatsAppRecordatorio = (consulta) => {
    const numero = consulta.paciente.telefono.replace(/\D/g, ""); // Limpia el número
    const mensaje = encodeURIComponent(generarMensajeRecordatorio(consulta));
    return `https://wa.me/${numero}?text=${mensaje}`;
  };
  const generarMensajeCancelacion = (consulta) => {
    return `Estimado/a ${consulta.paciente.nombre_paciente}, 
Lamentamos informarle que su consulta programada para el día ${consulta.horario.fecha} a las ${consulta.horario.hora} con la  ${selectedOdontologa ? odontologas.find((od) => od.id === selectedOdontologa)?.nombre : ""} ha sido cancelada. 
Por favor, comuníquese con nosotros para reprogramar su cita. 
Disculpe las molestias ocasionadas. 
Saludos cordiales.`;
  };

  const obtenerLinkWhatsAppCancelacion = (consulta) => {
    const numero = consulta.paciente.telefono.replace(/\D/g, ""); // Limpia el número
    const mensaje = encodeURIComponent(generarMensajeCancelacion(consulta));
    return `https://wa.me/${numero}?text=${mensaje}`;
  };

  const mostrarCamposPaciente = () => {
    if (!formModalData) return null;

    return (
      <>
        <input
          type="text"
          className="form-control bg-light border-0 mb-3"
          placeholder="DNI"
          value={dni}
          onChange={handleDniChange}
          onKeyDown={handleKeyDown} // Buscar paciente al presionar Enter
          onBlur={handleBlur} // Buscar paciente al salir del campo
        />

        {dniVerificado && (
          <>
            {pacienteExistente ? (
              <>
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Nombre"
                  value={pacienteExistente.nombre_paciente}
                  readOnly
                />
                <input
                  type="email"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Correo"
                  value={pacienteExistente.correo}
                  readOnly
                />
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Teléfono"
                  value={pacienteExistente.telefono}
                  readOnly
                />
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Domicilio"
                  value={pacienteExistente.domicilio || ""}
                  readOnly
                />
              </>
            ) : (
              <>
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Nombre"
                  value={nuevoPaciente.nombre}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, nombre: e.target.value })}
                />
                <input
                  type="email"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Correo"
                  value={nuevoPaciente.correo}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, correo: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Teléfono"
                  value={nuevoPaciente.telefono}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, telefono: e.target.value })}
                />
                <input
                  type="text"
                  className="form-control bg-light border-0 mb-3"
                  placeholder="Domicilio"
                  value={nuevoPaciente.domicilio}
                  onChange={(e) => setNuevoPaciente({ ...nuevoPaciente, domicilio: e.target.value })}
                />
              </>
            )}
          </>
        )}
      </>
    );
  };

  const getHoursByTurno = () => {
    return turno === "mañana"
      ? ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30"]
      : ["08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30"];
  };

  return (
    <Container fluid className="bg-dark min-vh-100 m-0 p-4">
      <Card className="p-3 bg-dark">
        {(idUsuario === 5 || idUsuario === 6) && (
          <Row className="justify-content-center">
            {odontologas.map((od, index) => (
              <Col key={od.id} xs={6} sm={4} md="auto" className="mb-2 text-center">
                <Button
                  variant={selectedOdontologa === od.id ? "light" : "dark"}
                  onClick={() => setSelectedOdontologa(od.id)}
                >
                  {od.nombre}
                </Button>
              </Col>
            ))}
          </Row>
        )}

        {idUsuario !== 5 && idUsuario !== 6 && (
          <h2 className="text-center text-white mt-3">
            Horarios de {selectedOdontologa ? odontologas.find((od) => od.id === selectedOdontologa)?.nombre : ""}
          </h2>
        )}
      </Card>

      <div className="d-flex justify-content-between mb-3">
        <button
          className="btn btn-primary"
          onClick={() => setStartDate(subDays(startDate, isMobile ? 1 : 7))}
          disabled={startDate <= new Date()}
        >
          ← Anterior
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setStartDate(addDays(startDate, isMobile ? 1 : 7))}
        >
          Siguiente →
        </button>
      </div>
      <Row className="gx-3 bg-light">
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Hora</th>
                {Array.from({ length: isMobile ? 1 : 7 }, (_, i) =>
                  addDays(startDate, i)
                ).map((day) => (
                  <th key={day}>{format(day, "EEE dd/MM", { locale: es })}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getHoursByTurno().map((time) => (
                <tr key={time}>
                  <td>{time}</td>
                  {Array.from({ length: window.innerWidth <= 768 ? 1 : 7 }, (_, i) => addDays(startDate, i)).map((day) => {
                    const dayString = format(day, "yyyy-MM-dd");
                    const horario = horarios.find((h) => h.fecha === dayString && h.hora.substring(0, 5) === time);

                    return (
                      <td
                        key={`${dayString}-${time}`}
                        className={horario ? (horario.estado === "disponible" ? "bg-primary text-dark" : "bg-secondary text-dark") : "transparent"}
                        style={{ cursor: "pointer" }}
                        onClick={() => handleHorarioClick(horario, dayString, time)}
                      >
                        {horario ? (horario.estado === "agendado" ? horario.paciente?.nombre_paciente || "Agendado" : "Disponible") : ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {modalData && (
          <Modal show={!!modalData} onHide={() => setModalData(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Detalles de la Consulta</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                <strong>Paciente:</strong> {modalData.paciente?.nombre_paciente || "No disponible"}
              </p>
              <p>
                <strong>DNI:</strong> {modalData.paciente?.dni_paciente || "No disponible"}
              </p>
              <p>
                <strong>Descripción:</strong> {modalData.consulta.descripcion || "-"}
              </p>
              <p>
                <strong>Fecha:</strong> {modalData.horario?.fecha || "No disponible"}
              </p>
              <p>
                <strong>Hora:</strong> {modalData.horario?.hora || "No disponible"}
              </p>
              <p>
                <strong>Fecha en que se agendó:</strong> {modalData.consulta.createdAt || "No disponible"}
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModalData(null)} style={{ cursor: "pointer" }}>
                Cerrar
              </Button>
              <Button variant="danger" onClick={handleAbrirCancelModal} style={{ cursor: "pointer" }}>
                Cancelar Consulta
              </Button>
              <Button
                variant="success"
                onClick={() => window.open(obtenerLinkWhatsAppRecordatorio(modalData), "_blank")}
                style={{ cursor: "pointer" }}
              >
                Recordar por WhatsApp
              </Button>
            </Modal.Footer>
          </Modal>)}
        {confirmModalData && (
          <Modal show={!!confirmModalData} onHide={() => setConfirmModalData(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Habilitar Horario</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                ¿Desea habilitar el horario de fecha {confirmModalData.fecha} y hora {confirmModalData.hora}?
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirmModalData(null)} style={{ cursor: "pointer" }}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleConfirmHabilitarHorario} style={{ cursor: "pointer" }}>
                Habilitar
              </Button>
            </Modal.Footer>
          </Modal>
        )}
        {availableModalData && (
          <Modal show={!!availableModalData} onHide={() => setAvailableModalData(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Horario Disponible</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Button variant="primary" onClick={handleAbrirFormModal} style={{ cursor: "pointer" }}>
                Agendar Nueva Consulta
              </Button>
              <Button variant="danger" onClick={handleDeshabilitarHorario} style={{ cursor: "pointer" }}>
                Eliminar Horario
              </Button>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setAvailableModalData(null)} style={{ cursor: "pointer" }}>
                Cerrar
              </Button>
            </Modal.Footer>
          </Modal>
        )}
        {formModalData && (
        <Modal show={!!formModalData} onHide={() => setFormModalData(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Agendar Consulta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {mostrarCamposPaciente()}
            <input
              type="text"
              className="form-control bg-light border-0 mb-3"
              placeholder="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setFormModalData(null)} style={{ cursor: "pointer" }}>
              Cerrar
            </Button>
            <Button variant="primary" onClick={agendarConsulta} style={{ cursor: "pointer" }}>
              Agendar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {cancelModalData && (
        <Modal show={!!cancelModalData} onHide={() => setCancelModalData(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Cancelar Consulta</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              ¿Desea eliminar la consulta del paciente {cancelModalData.paciente.nombre_paciente} del día {cancelModalData.horario.fecha} y hora {cancelModalData.horario.hora}?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setCancelModalData(null)} style={{ cursor: "pointer" }}>
              No
            </Button>
            <Button variant="danger" onClick={handleCancelarConsulta} style={{ cursor: "pointer" }}>
              Sí
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {cancelSuccessModalData && (
        <Modal show={!!cancelSuccessModalData} onHide={() => setCancelSuccessModalData(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Consulta Cancelada</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              La consulta del paciente {cancelSuccessModalData.paciente.nombre_paciente} prevista para el día {cancelSuccessModalData.horario.fecha} a las {cancelSuccessModalData.horario.hora} ha sido cancelada.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => setCancelSuccessModalData(null)} href={obtenerLinkWhatsAppCancelacion(cancelSuccessModalData)} target="_blank" style={{ cursor: "pointer" }}>
              Enviar WhatsApp de Cancelación
            </Button>
            <Button variant="secondary" onClick={() => setCancelSuccessModalData(null)} style={{ cursor: "pointer" }}>
              Cerrar
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      </Row>
    </Container>
  );
};

export default Horarios;
